import {
  Configure,
  Hits,
  useConnector,
  useSortBy,
  SortBy,
  useSearchBox,
} from 'react-instantsearch-hooks-web';
import type {
  StatsConnectorParams,
  StatsWidgetDescription,
} from 'instantsearch.js/es/connectors/stats/connectStats';
import connectStats from 'instantsearch.js/es/connectors/stats/connectStats';
import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Field, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { useEffect } from 'react';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import classnames from 'tailwindcss-classnames';
import { REPORTS_SEARCH_INDEX } from 'lib/util/algolia';
import { CTInstructionHit } from './CTInstructionHit';
import { Autocomplete } from 'components/helpers/AlgoliaSearch/Autocomplete';
import { CustomPagination } from 'components/helpers/AlgoliaSearch/CustomPagination';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';

export type CTInstructionsProps = Sitecore.Override.ComponentBase &
  Components.General.Ctinstructions.Fields.CTInstructions;

const errorMessageClass = classnames(
  'bg-red-destructive',
  'bg-opacity-[15%]',
  'py-5',
  'pl-4',
  'rte'
);

export type UseStatsProps = StatsConnectorParams;

export type CTInstructionsSearchProps = CTInstructionsProps & UseStatsProps;

function useStats(props?: UseStatsProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useConnector<StatsConnectorParams, StatsWidgetDescription>(connectStats as any, props);
}

const filter = 'reportTypeFacet:"CT Instructions" OR reportTypeFacet:"Cooperative Instructions"';

const CTInstructionsWithSearch = ({ fields, ...props }: CTInstructionsSearchProps): JSX.Element => {
  const reportSearchIndex = `${REPORTS_SEARCH_INDEX()}_date_desc`;
  const { refine } = useSortBy({
    items: [{ label: 'reports', value: reportSearchIndex }],
  });
  const { query } = useSearchBox();

  const isBrowser = typeof window !== 'undefined'; // Check if window is defined

  useEffect(() => {
    if (isBrowser) {
      const delay = 100;
      const timer = setTimeout(() => {
        refine(reportSearchIndex);
      }, delay);
      return () => clearTimeout(timer);
    }
    return () => 0;
  }, [refine, isBrowser, reportSearchIndex]);

  const { nbHits } = useStats(props);
  const showPagination = nbHits > 10;
  const notesMessage = fields?.searchNote?.value || ``;
  const errorMessage = (fields?.errorMessageText?.value || ``).replace(`{centerId}`, query);
  const errorField: Field<string> = {
    value: errorMessage,
  };

  const noteClass = classnames('mb-2', 'text-sm', 'md:w-2/5', 'leading-[15px]');

  return (
    <>
      <Configure filters={filter} analytics={false} hitsPerPage={10} />
      <SortBy
        classNames={{ root: classnames('hidden') }}
        items={[{ label: `default`, value: reportSearchIndex }]}
      />
      <div className={classnames('nrc-ct-instr')}>
        <Autocomplete detachedMediaQuery="none" openOnFocus asSearchBox skipSuggestions />
      </div>
      <p className={noteClass}>{notesMessage}</p>
      {query.length > 0 && nbHits > 0 && (
        <div className={classnames('ct-Instructions')}>
          <div>
            <Hits hitComponent={CTInstructionHit} />
          </div>
          {showPagination && (
            <div>
              <CustomPagination padding={1} />
            </div>
          )}
        </div>
      )}
      {query.length > 0 && nbHits === 0 && (
        <RichTextA11yWrapper className={errorMessageClass} field={errorField} />
      )}
    </>
  );
};

const CTInstructions = ({ fields, ...props }: CTInstructionsProps): JSX.Element => {
  const pageEditing = useExperienceEditor();

  if (!fields) {
    return <MissingDataSource />;
  }

  const containerClass = classnames(
    'mx-auto',
    'w-full',
    'relative',
    'py-4',
    'mt-0',
    'md:px-0',
    'md:py-6',
    'overflow-hidden'
  );
  const labelClass = classnames('text-black', 'mb-2', 'block', 'font-semibold');

  return (
    <div
      data-component="authorable/NetworkResourceCenter/CTInstructions"
      className={containerClass}
    >
      <Text field={fields.searchLabel} tag="label" className={labelClass} />
      {(pageEditing && (
        <div>
          <div>CT Search Components go here</div>
          <Text field={fields.errorMessageText} tag="p" className={errorMessageClass} />
        </div>
      )) || <CTInstructionsWithSearch fields={fields} {...props} />}
      <hr className={classnames('h-1', 'my-4', 'border-b', 'border-t-0', 'border-gray-midGray')} />
    </div>
  );
};

export default CTInstructions;
